import { css } from "@emotion/react";
import { LinkIcon } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import type { ImmutableObject } from "@/lib/immutable";
import type { Catalog } from "../../hooks/useCatalogState";
import {
	type ProductCategoriesTree,
	type ProductCategory,
	type ProductDepartment,
	type ProductSubCategory,
	useInventoryState,
} from "../../hooks/useInventoryState";
import { useStorefrontSettingsState } from "../../hooks/useStorefrontSettingsState";
import { useThemeState } from "../../hooks/useThemeState";
import { storefrontSdk } from "../../storefrontsdk";

//-------------------------------------------------------------------------------------------------------
export type CategoryTreeGridProps = {
	catalog?: Catalog | ImmutableObject<Catalog>;
	rentalCategoriesTree?: ProductCategoriesTree;
	showTitle?: boolean;
};

export const CategoryTreeGrid: React.FC<CategoryTreeGridProps> = (props) => {
	const themeState = useThemeState();
	const isDarkTheme = themeState.isDark();
	const inventoryState = useInventoryState();
	const storefrontSettingsState = useStorefrontSettingsState();
	const landingPageCategoryBrowserItemWidth =
		storefrontSettingsState.get().settings
			?.LandingPageCategoryBrowserItemWidth ?? "xl";
	const expandCategoriesByDefault =
		storefrontSettingsState.get().settings
			?.LandingPageExpandInventoryTypesCategoriesAndSubCategoriesByDefault ??
		false;
	let landingPageCategoryBrowserItemWidthPercent = 100;
	if (landingPageCategoryBrowserItemWidth === "sm") {
		landingPageCategoryBrowserItemWidthPercent = 20;
	} else if (landingPageCategoryBrowserItemWidth === "md") {
		landingPageCategoryBrowserItemWidthPercent = 30;
	} else if (landingPageCategoryBrowserItemWidth === "lg") {
		landingPageCategoryBrowserItemWidthPercent = 45;
	} else if (landingPageCategoryBrowserItemWidth === "xl") {
		landingPageCategoryBrowserItemWidthPercent = 100;
	}

	// Extract complex expressions for useEffect dependencies
	const catalogId = props.catalog?.Id;

	// Local state for category tree data
	const [categoryTreeData, setCategoryTreeData] =
		React.useState<ProductCategoriesTree | null>(null);
	const [isLoading, setIsLoading] = React.useState(false);
	const [retryCount, setRetryCount] = React.useState(0);

	// Get departments from props, inventory state, or local state (in order of preference)
	const departments =
		props.rentalCategoriesTree?.Departments ||
		(catalogId && inventoryState.get().catalog?.Id === catalogId
			? (inventoryState.get().productCategoriesTree
					?.Departments as ProductDepartment[])
			: undefined) ||
		categoryTreeData?.Departments;

	const hasInventoryData = React.useMemo(() => {
		return (
			catalogId &&
			inventoryState.get().catalog?.Id === catalogId &&
			inventoryState.get().productCategoriesTree?.Departments
		);
	}, [catalogId, inventoryState]);

	const shouldLoadCategoryTree = React.useMemo(() => {
		return (
			catalogId &&
			!props.rentalCategoriesTree &&
			!categoryTreeData &&
			!hasInventoryData &&
			retryCount < 3
		);
	}, [
		catalogId,
		props.rentalCategoriesTree,
		categoryTreeData,
		hasInventoryData,
		retryCount,
	]);

	// Load category tree data for this catalog if not provided
	React.useEffect(() => {
		// Reset retry count when catalog changes
		if (catalogId && retryCount > 0) {
			setRetryCount(0);
		}

		if (shouldLoadCategoryTree) {
			const loadCategoryTree = async () => {
				setIsLoading(true);
				try {
					if (catalogId === "") {
						return;
					}
					const getCategoryTreeResponse =
						await storefrontSdk.storefrontV1GetCategoryTreeAsync(
							catalogId || "",
						);
					if ((await getCategoryTreeResponse).status === 200) {
						if ((getCategoryTreeResponse.data?.Departments?.length || 0) > 0) {
							const productDepartments: ProductDepartment[] = [];
							getCategoryTreeResponse.data.Departments?.forEach(
								(department) => {
									const categories: ProductCategory[] = [];
									department.Categories?.forEach((category) => {
										const subcategories: ProductSubCategory[] = [];
										category.SubCategories?.forEach((subcategory) => {
											subcategories.push({
												Id: subcategory.Id || "",
												LinkUrl: subcategory.LinkUrl || "",
												Name: subcategory.Name || "",
												UrlName: subcategory.UrlName || "",
											});
										});
										categories.push({
											Id: category.Id || "",
											Name: category.Name || "",
											LinkUrl: category.LinkUrl || "",
											UrlName: category.UrlName || "",
											SubCategories: subcategories,
										});
									});
									productDepartments.push({
										Id: department.Id || "",
										Name: department.Name || "",
										hasExpander: undefined,
										UrlName: department.UrlName || "",
										Categories: categories,
									});
								},
							);
							const categoryTree: ProductCategoriesTree = {
								Departments: productDepartments,
							};
							setCategoryTreeData(categoryTree);
						} else {
							console.warn(
								"CategoryTreeGrid: No departments found in response",
							);
						}
					} else {
						console.error(
							"CategoryTreeGrid: Failed to load category tree, status:",
							getCategoryTreeResponse.status,
						);
						// Retry on failure
						if (retryCount < 2) {
							setTimeout(() => {
								setRetryCount((prev) => prev + 1);
							}, 1000);
						}
					}
				} catch (error) {
					console.error(
						"CategoryTreeGrid: Failed to load category tree:",
						error,
					);
					// Retry on error
					if (retryCount < 2) {
						setTimeout(() => {
							setRetryCount((prev) => prev + 1);
						}, 1000);
					}
				} finally {
					setIsLoading(false);
				}
			};

			// Add a small delay to ensure the component is fully mounted
			const timer = setTimeout(() => {
				loadCategoryTree();
			}, 200);

			return () => clearTimeout(timer);
		}
	}, [catalogId, shouldLoadCategoryTree, retryCount]);

	// Reset data when catalog changes
	React.useEffect(() => {
		if (catalogId) {
			setCategoryTreeData(null);
			setRetryCount(0);
		}
	}, [catalogId]);

	// Also trigger loading if inventory state has the catalog but no category tree data
	React.useEffect(() => {
		const inventoryCatalogId = inventoryState.get().catalog?.Id;
		const hasInventoryCategoryTree =
			inventoryState.get().productCategoriesTree?.Departments;
		const isLoadingCategoryTree = inventoryState.get().loadingCategoryTree;

		if (
			catalogId &&
			inventoryCatalogId === catalogId &&
			!hasInventoryCategoryTree &&
			!isLoadingCategoryTree &&
			!props.rentalCategoriesTree &&
			!categoryTreeData
		) {
			// Try to load the category tree through the inventory state first
			inventoryState.loadProductCategoryTreeAsync(catalogId);
		}
	}, [catalogId, inventoryState, props.rentalCategoriesTree, categoryTreeData]);

	// Don't render if no catalog
	if (!catalogId) {
		return null;
	}

	// Show loading if departments are not available yet
	const isInventoryLoading = inventoryState.get().loadingCategoryTree;
	const shouldShowLoading = isLoading || isInventoryLoading;
	const hasNoData = !departments || departments.length === 0;

	if (shouldShowLoading || hasNoData) {
		return (
			<div
				css={css`
                margin: 2rem 0 0 0;
                padding: 2rem 1rem;
                border-top: ${isDarkTheme ? "1px solid rgba(255,255,255,.1)" : "1px solid rgba(0,0,0,.1)"};
                text-align: center;
                color: ${isDarkTheme ? "#ccc" : "#666"};
            `}
			>
				{shouldShowLoading
					? "Loading categories..."
					: "No categories available"}
			</div>
		);
	}

	return (
		<div
			css={css`
            margin: 0rem 0 0 0;
            padding: 2rem 1rem;
            /*border-top: ${isDarkTheme ? "1px solid rgba(255,255,255,.1)" : "1px solid rgba(0,0,0,.1)"};*/
            width: 100%;
            box-sizing: border-box;
            overflow-x: hidden;
        `}
		>
			{/*props.showTitle && (
                <div css={css`
                    text-align: center;
                    font-size: 1.2em;
                    font-weight: 500;
                    margin-bottom: 2rem;
                    color: ${isDarkTheme ? '#fff' : '#333'};
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                `}>
                    Browse Categories
                </div>
            )*/}

			<div
				css={css`
                display: flex;
                flex-wrap: wrap;
                gap: 1.5rem;
                width: 100%;
                max-width: 1200px;
                margin: 0 auto;
                
                @media (max-width: 768px) {
                    gap: 1rem;
                }
                
                @media (max-width: 480px) {
                    gap: 0.75rem;
                }
                
                @media (max-width: 360px) {
                    gap: 0.5rem;
                }
            `}
			>
				{departments.map(
					(
						department: ProductDepartment | ImmutableObject<ProductDepartment>,
					) => (
						<div
							key={department.Id}
							css={css`
                        display: flex;
                        flex-direction: column;
                        flex: 1 1 ${landingPageCategoryBrowserItemWidthPercent}%;
                        
                    `}
						>
							<div
								css={css`
                             font-size: 0.9em;
                             font-weight: 600;
                             margin-bottom: 1rem;
                             color: ${isDarkTheme ? "#fff" : "#333"};
                             text-transform: uppercase;
                             letter-spacing: 0.05em;
                             border-bottom: 1px solid ${isDarkTheme ? "rgba(255,255,255,.2)" : "rgba(0,0,0,.2)"};
                             padding-bottom: 0.5rem;
                             white-space: nowrap;
                             @media (max-width: 480px) {
                                 font-size: 0.8em;
                                 margin-bottom: 0.75rem;
                             }
                         `}
							>
								{department.Name}
							</div>

							<div
								css={css`
                            display: flex;
                            flex-direction: column;
                            gap: 1rem;
                        `}
							>
								{department.Categories?.map(
									(
										category:
											| ProductCategory
											| ImmutableObject<ProductCategory>,
									) => (
										<div key={category.Id} css={css`padding:1px 0`}>
											{expandCategoriesByDefault &&
											category.SubCategories &&
											category.SubCategories.length > 0 ? (
												// Category with subcategories
												<div
													css={css`
                                            margin-bottom: 0.75rem;
                                        `}
												>
													<div
														css={css`
                                                font-weight: 500;
                                                color: ${isDarkTheme ? "#777" : "#666"};
                                                margin-bottom: 0.5rem;
                                                font-size: 0.7rem;
                                                text-transform: uppercase;
                                                letter-spacing: 0.03em;
                                                
                                                @media (max-width: 480px) {
                                                    margin-bottom: 0.4rem;
                                                }
                                            `}
													>
														{category.Name}
													</div>
													<div
														css={css`
                                                display: flex;
                                                flex-direction: column;
                                                gap: 0.25rem;
                                                margin-left: 0.5rem;
                                            `}
													>
														{category.SubCategories.map(
															(
																subCategory:
																	| ProductSubCategory
																	| ImmutableObject<ProductSubCategory>,
															) => (
																<Link
																	key={subCategory.Id}
																	to={`/inventory/${catalogId}/${department.Id}/${category.Id}/${subCategory.Id}`}
																	css={css`
                                                            display: flex;
                                                            align-items: center;
                                                            color: ${isDarkTheme ? "#aaa" : "#777"};
                                                            text-decoration: none;
                                                            font-size: 1rem;
                                                            line-height: 1.4;
                                                            transition: all 0.2s ease;
                                                            padding: 0.1rem 0;
                                                            
                                                            &:hover {
                                                                color: ${isDarkTheme ? "#fff" : "#333"};
                                                                text-decoration: none;
                                                                transform: translateX(2px);
                                                            }
                                                        `}
																	onClick={() => {
																		// Clear search text when subcategory is clicked
																		inventoryState.clearSearchTextAsync();
																	}}
																>
																	<LinkIcon
																		size="12"
																		css={css`padding-right: 4px`}
																	/>{" "}
																	{subCategory.Name}
																</Link>
															),
														)}
													</div>
												</div>
											) : (
												// Category without subcategories
												<Link
													to={`/inventory/${catalogId}/${department.Id}/${category.Id}`}
													css={css`
                                            display: flex;
                                                            align-items: center;
                                                color: ${isDarkTheme ? "#aaa" : "#777"};
                                                text-decoration: none;
                                                font-size: 1rem;
                                                line-height: 1.4;
                                                transition: all 0.2s ease;
                                                padding: 0.1rem 0;
                                                
                                                &:hover {
                                                    color: ${isDarkTheme ? "#fff" : "#333"};
                                                    text-decoration: none;
                                                    transform: translateX(2px);
                                                }
                                            `}
													onClick={() => {
														// Clear search text when category is clicked
														inventoryState.clearSearchTextAsync();
													}}
												>
													<LinkIcon size="12" css={css`padding-right: 4px`} />
													{category.Name}
												</Link>
											)}
										</div>
									),
								)}
							</div>
						</div>
					),
				)}
			</div>
		</div>
	);
};
